import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { safeBlobGet } from '../utils/fetchPdfBlob';
import { useAuth } from '../contexts/AuthContext';
import { AppShell } from '../components/AppShell';
import { GabryxFooter } from '../components/GabryxBranding';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import {
  FilePdf, FileDoc, DownloadSimple, ArrowLeft,
  Files, FileText, Presentation, UserCircle, Globe,
  Play
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export function PresentationPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [personalized, setPersonalized] = useState(false);
  const [downloading, setDownloading] = useState(null);
  const [hasSettings, setHasSettings] = useState(false);
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (user && !isAdmin) {
      navigate('/dashboard', { replace: true });
    }
  }, [user, isAdmin, navigate]);

  const fetchCompanySettings = useCallback(() => {
    axios.get(`${API}/company-settings`, { withCredentials: true })
      .then(res => {
        if (res.data?.company_name) setHasSettings(true);
      })
      .catch((_e) => { void _e; });
  }, [setHasSettings]);

  useEffect(() => { fetchCompanySettings(); }, [fetchCompanySettings]);

  const download = async (version, format) => {
    const key = `${version}-${format}`;
    setDownloading(key);
    try {
      const url = `${API}/presentation/${version}/${format}?personalized=${personalized}`;
      const res = await safeBlobGet(url);
      const blob = new Blob([res.data]);
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      const vLabel = version === 'short' ? 'Synthetique' : 'Complete';
      const ext = format;
      a.download = `CHANTIX_Presentation_${vLabel}.${ext}`;
      a.click();
      window.URL.revokeObjectURL(a.href);
      toast.success(`${format.toUpperCase()} téléchargé`);
    } catch (_err) { toast.error(_err.message || 'Erreur de téléchargement'); }
    finally { setDownloading(null); }
  };

  return (
    <AppShell>
      <div className="space-y-6 max-w-3xl mx-auto" data-testid="presentation-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="h-10 w-10 md:hidden flex-shrink-0"
            onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft size={22} />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-[#111827]" data-testid="presentation-title">
              Presentation CHANTIX
            </h1>
            <p className="text-sm text-[#4B5563]">Documents de presentation commerciale et guide utilisateur</p>
          </div>
        </div>

        {/* Interactive slideshow - Admin only */}
        {isAdmin && (
          <Card className="border-[#FF6B00]/30 bg-gradient-to-r from-orange-50 to-white" data-testid="slideshow-card">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 rounded-xl bg-[#FF6B00] flex items-center justify-center flex-shrink-0">
                    <Presentation size={28} className="text-white" weight="fill" />
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-bold text-[#111827]">Mode presentation interactive</h2>
                    <p className="text-sm text-[#4B5563] mt-0.5">Parcours guide type diaporama pour vos rendez-vous clients</p>
                  </div>
                </div>
                <Button onClick={() => navigate('/slideshow')}
                  className="h-12 px-6 bg-[#FF6B00] hover:bg-orange-700 gap-2 text-base flex-shrink-0"
                  data-testid="launch-slideshow-btn">
                  <Play size={20} weight="fill" /> Lancer
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Personalization toggle */}
        <Card className="border-orange-200 bg-orange-50/50">
          <CardContent className="p-4 sm:p-5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {personalized ? (
                  <UserCircle size={24} className="text-[#FF6B00]" weight="fill" />
                ) : (
                  <Globe size={24} className="text-gray-500" />
                )}
                <div>
                  <Label className="text-sm font-semibold text-[#111827]">
                    {personalized ? 'Version personnalisee' : 'Version generique'}
                  </Label>
                  <p className="text-xs text-[#4B5563] mt-0.5">
                    {personalized
                      ? 'Inclut vos coordonnées et informations entreprise'
                      : 'Document standard sans coordonnées personnelles'}
                  </p>
                </div>
              </div>
              <Switch
                checked={personalized}
                onCheckedChange={setPersonalized}
                data-testid="personalized-toggle"
              />
            </div>
            {personalized && !hasSettings && (
              <div className="mt-3 p-2 bg-amber-100 rounded-lg text-xs text-amber-800 flex items-center gap-2">
                <span>Renseignez vos informations dans</span>
                <Button variant="link" size="sm" className="h-auto p-0 text-xs text-amber-900 underline"
                  onClick={() => navigate('/settings')} data-testid="go-settings-btn">
                  Paramètres
                </Button>
                <span>pour personnaliser le document.</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Short version */}
        <Card className="border hover:border-[#FF6B00]/30 transition-colors" data-testid="card-short-version">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center flex-shrink-0">
                <FileText size={28} className="text-[#FF6B00]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-[#111827]">Version synthetique</h2>
                <p className="text-sm text-[#4B5563] mt-1">
                  2 pages — Idéal pour un envoi rapide à un prospect ou une première prise de contact.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Presentation</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Fonctionnalites</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Avantages</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => download('short', 'pdf')} disabled={!!downloading}
                className="flex-1 h-12 bg-[#FF6B00] hover:bg-orange-700 text-sm sm:text-base"
                data-testid="download-short-pdf">
                {downloading === 'short-pdf' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePdf size={20} className="mr-2" />}
                Télécharger PDF
              </Button>
              <Button onClick={() => download('short', 'docx')} disabled={!!downloading}
                variant="outline" className="flex-1 h-12 text-blue-600 border-blue-200 hover:bg-blue-50 text-sm sm:text-base"
                data-testid="download-short-docx">
                {downloading === 'short-docx' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDoc size={20} className="mr-2" />}
                Télécharger Word
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Full version */}
        <Card className="border hover:border-[#FF6B00]/30 transition-colors" data-testid="card-full-version">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-orange-200 to-orange-100 flex items-center justify-center flex-shrink-0">
                <Files size={28} className="text-[#FF6B00]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-base sm:text-lg font-bold text-[#111827]">Version complete</h2>
                <p className="text-sm text-[#4B5563] mt-1">
                  5-8 pages — Pour un rendez-vous client, un dossier de candidature ou renforcer votre credibilite.
                </p>
                <div className="flex flex-wrap gap-1.5 mt-2">
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Presentation</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Fonctionnalites</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Workflow</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Mobile</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Avantages</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Roadmap</span>
                  <span className="px-2 py-0.5 rounded text-xs bg-gray-100 text-gray-600">Contact</span>
                </div>
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={() => download('full', 'pdf')} disabled={!!downloading}
                className="flex-1 h-12 bg-[#FF6B00] hover:bg-orange-700 text-sm sm:text-base"
                data-testid="download-full-pdf">
                {downloading === 'full-pdf' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FilePdf size={20} className="mr-2" />}
                Télécharger PDF
              </Button>
              <Button onClick={() => download('full', 'docx')} disabled={!!downloading}
                variant="outline" className="flex-1 h-12 text-blue-600 border-blue-200 hover:bg-blue-50 text-sm sm:text-base"
                data-testid="download-full-docx">
                {downloading === 'full-docx' ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileDoc size={20} className="mr-2" />}
                Télécharger Word
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Info section */}
        <div className="text-center text-xs text-[#9CA3AF] pb-6 space-y-2">
          <GabryxFooter variant="presentation" />
          <p className="mt-2">Les documents sont générés dynamiquement avec vos informations entreprise actuelles.</p>
          <p>Modifiéz vos coordonnées dans <button onClick={() => navigate('/settings')} className="text-[#FF6B00] underline">Paramètres</button> pour mettre a jour.</p>
        </div>
      </div>
    </AppShell>
  );
}
